<template>
    <div class="row banner-text">
        <div class="col-lg-6 offset-lg-3 col-xs-12">
            <div class="card">
                <div class="card-body">
                    <validation-observer
                        slim
                        ref="form"
                        v-slot="{ handleSubmit, invalid }"
                    >
                        <b-form
                            id="intolerance-form"
                            @submit.prevent="handle(handleSubmit, invalid)"
                            novalidate
                        >
                            <h1 class="text-center">
                                {{ $t("pages.forms.titles.intolerance") }}
                            </h1>

                            <b-alert
                                v-model="showTopBannerForSeconds"
                                class="position-fixed fixed-top m-0 rounded-0"
                                style="z-index: 2000;"
                                variant="danger"
                                dismissible
                            >
                                <h4 class="mb-0">
                                    <span
                                        class="text-danger font-weight-bolder font-italic"
                                        >{{
                                            $t(
                                                "pages.forms.alerts.red-fields.banner-text"
                                            )
                                        }}</span
                                    >
                                </h4>
                            </b-alert>

                            <b-alert variant="info" :show="inReview">
                                <h4>
                                    <i class="fas fa-bell" />
                                    {{ $t("pages.forms.alerts.review.title") }}
                                </h4>
                                <p>
                                    {{
                                        $t(
                                            "pages.forms.alerts.review.description"
                                        )
                                    }}
                                </p>
                            </b-alert>

                            <h2 id="form-start">
                                {{ $t("pages.forms.headers.sample") }}
                            </h2>

                            <sample-id
                                v-model="form.sample"
                                required
                                :reviewing="inReview"
                            />
                            <sent-date
                                v-model="form.sentDate"
                                required
                                :reviewing="inReview"
                            />
                            <client-type
                                v-model="form.client_type"
                                required
                                :in-review="inReview"
                            />

                            <div v-if="isAssociatedCenter">
                                <h2>{{ $t("pages.forms.headers.center") }}</h2>

                                <clinic-name
                                    v-model="form.clinicName"
                                    :required="isAssociatedCenter"
                                    :reviewing="inReview"
                                />
                                <clinic-phone
                                    v-model="form.clinicPhone"
                                    :required="isAssociatedCenter"
                                    :reviewing="inReview"
                                />
                                <results-email
                                    v-model="form.resultsEmail"
                                    :required="isAssociatedCenter"
                                    :reviewing="inReview"
                                />
                            </div>

                            <h2>{{ $t("pages.forms.headers.general") }}</h2>

                            <name
                                v-model="form.name"
                                required
                                :reviewing="inReview"
                            />
                            <surname
                                v-model="form.surname"
                                required
                                :reviewing="inReview"
                            />
                            <gender
                                v-model="form.gender"
                                required
                                :in-review="inReview"
                            />
                            <patient-email
                                v-model="form.patientEmail"
                                required
                                :reviewing="inReview"
                            />
                            <div v-if="isAssociatedCenter">
                                <phone
                                    v-model="form.phone"
                                    required
                                    :reviewing="inReview"
                                />
                            </div>
                            <birth-date
                                v-model="form.birthdate"
                                required
                                :reviewing="inReview"
                            />
                            <div v-if="isAssociatedCenter">
                                <id-card-num
                                    v-model="form.idCardNum"
                                    required
                                    :reviewing="inReview"
                                />
                                <patient-address
                                    v-model="form.address"
                                    required
                                    :reviewing="inReview"
                                />
                                <city
                                    v-model="form.city"
                                    required
                                    :reviewing="inReview"
                                />
                                <state
                                    v-model="form.state"
                                    required
                                    :reviewing="inReview"
                                />
                                <zip-code
                                    v-model="form.zipcode"
                                    required
                                    :reviewing="inReview"
                                />

                                <country
                                    v-model="form.country"
                                    required
                                    :reviewing="inReview"
                                />
                            </div>

                            <h2>
                                {{
                                    $t(
                                        "pages.forms.headers.intolerance.test-type"
                                    )
                                }}
                            </h2>

                            <test-acquired
                                v-model="form.testAcquired"
                                required
                                :reviewing="inReview"
                            />

                            <gdpr-checkbox v-model="conditionsAccepted" />

                            <b-button-toolbar
                                style="justify-content: center; display: flex;"
                            >
                                <b-button-group v-show="inReview" class="mx-1">
                                    <b-button
                                        type="button"
                                        :disabled="alreadySent"
                                        @click.prevent="inReview = false"
                                        >{{ backButtonText }}</b-button
                                    >
                                </b-button-group>
                                <b-button-group class="mx-1">
                                    <b-button
                                        id="sendBtn"
                                        type="submit"
                                        variant="info"
                                        style="color: white;"
                                        :disabled="
                                            !conditionsAccepted || alreadySent
                                        "
                                    >
                                        <b-spinner
                                            small
                                            type="border"
                                            v-show="showSpinner"
                                        />
                                        {{ buttonText }}
                                    </b-button>
                                </b-button-group>
                            </b-button-toolbar>
                        </b-form>
                    </validation-observer>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import VueScrollTo from "vue-scrollto";
import FormInputHelpers from "../../mixins/FormInputHelpers";
import axios from "axios";
import { ValidationObserver } from "vee-validate";

export default {
    mixins: [FormInputHelpers],
    components: { ValidationObserver },
    data() {
        return {
            form: {
                clinicName: "",
                clinicPhone: {
                    clinicPhone: ""
                },
                resultsEmail: "",
                sentDate: "",
                sample: {
                    prefix: null,
                    id: null
                },
                name: "",
                surname: "",
                gender: "",
                patientEmail: "",
                phone: {
                    phone: ""
                },
                birthdate: "",
                idCardNum: "",
                address: "",
                city: "",
                state: "",
                zipcode: "",
                country: {
                    country: null,
                    code: null
                },
                testAcquired: null
            },
            conditionsAccepted: false,
            inReview: false,
            showSpinner: false,
            alreadySent: false,
            showTopBannerForSeconds: false,
            isAssociatedCenter: false,
            buttonText: this.$t("pages.forms.button-texts.next"),
            backButtonText: this.$t("pages.forms.button-texts.back")
        };
    },
    methods: {
        handle(handleSubmit, invalid) {
            if (invalid) {
                this.showTopBannerForSeconds = 5;
            }
            handleSubmit(this.submit);
        },
        trackReview() {
            this.$gtag.event("review-form", {
                event_category: "forms",
                event_label: "intolerance-form"
            });
        },
        trackSend() {
            this.$gtag.event("send-form", {
                event_category: "forms",
                event_label: "intolerance-form"
            });
        },
        trackSampleIdNotFound() {
            this.$gtag.event("sampleid-notfound", {
                event_category: "forms",
                event_label: "intolerance-form"
            });
        },
        trackSampleIdAlreadyRegistered() {
            this.$gtag.event("sampleid-alreadyregistered", {
                event_category: "forms",
                event_label: "intolerance-form"
            });
        },
        trackOtherFail() {
            this.$gtag.event("other-fail", {
                event_category: "forms",
                event_label: "intolerance-form"
            });
        },
        async submit(e) {
            if (!this.inReview) {
                this.inReview = true;
                this.trackReview();
                VueScrollTo.scrollTo(
                    document.getElementById("intolerance-form")
                );
                this.buttonText = this.$i18n.t("pages.forms.button-texts.send");
                return;
            }

            try {
                this.buttonText = this.$i18n.t(
                    "pages.forms.button-texts.sending-data"
                );
                this.showSpinner = true;

                await axios.post(
                    this.route("onegenlab.register.intolerance_response"),
                    {
                        sampleID: this.form.sample.prefix + this.form.sample.id,
                        sentDate: this.form.sentDate,
                        client_type: this.form.client_type,
                        clinicName: this.form.clinicName,
                        clinicPhone: this.form.clinicPhone.clinicPhone,
                        resultsEmail: this.form.resultsEmail,
                        name: this.form.name,
                        surname: this.form.surname,
                        gender: this.form.gender,
                        patientEmail: this.form.patientEmail,
                        phone: this.form.phone.phone,
                        birthdate: this.form.birthdate,
                        idCardNum: this.form.idCardNum,
                        address: this.form.address,
                        city: this.form.city,
                        state: this.form.state,
                        zipcode: this.form.zipcode,
                        country: this.form.country.text,
                        testAcquired: this.form.testAcquired.value
                    }
                );

                this.trackSend();
                this.alreadySent = true;

                this.$swal({
                    title: this.$i18n.t(
                        "pages.forms.alerts.swal.success.title"
                    ),
                    text: this.$i18n.t("pages.forms.alerts.swal.success.text"),
                    icon: "success",
                    showCancelButton: false,
                    timer: 6000
                }).then(function() {
                    window.location.replace("https://register.onegenlab.com");
                });
            } catch (e) {
                console.log(e);

                this.alreadySent = false;
                this.buttonText = this.$i18n.t("pages.forms.button-texts.next");
                this.showSpinner = false;

                if (e.response.status === 404) {
                    this.$swal({
                        title: this.$i18n.t(
                            "pages.forms.alerts.swal.error.not-exist.title"
                        ),
                        text: this.$i18n.t(
                            "pages.forms.alerts.swal.error.not-exist.text"
                        ),
                        icon: "error",
                        confirmButtonColor: "#DD6B55"
                    });
                    this.trackSampleIdNotFound();
                } else if (e.response.status === 409) {
                    this.$swal({
                        title: this.$i18n.t(
                            "pages.forms.alerts.swal.error.form-exist.title"
                        ),
                        text: this.$i18n.t(
                            "pages.forms.alerts.swal.error.form-exist.text"
                        ),
                        icon: "error",
                        confirmButtonColor: "#DD6B55"
                    });
                    this.trackSampleIdAlreadyRegistered();
                } else {
                    this.$swal({
                        title: this.$i18n.t(
                            "pages.forms.alerts.swal.error.other.title"
                        ),
                        text: this.$i18n.t(
                            "pages.forms.alerts.swal.error.other.text"
                        ),
                        icon: "error",
                        confirmButtonColor: "#DD6B55"
                    });
                    this.trackOtherFail();
                }
            }
        }
    },
    watch: {
        "form.client_type": function(newClientType, oldClientType) {
            this.isAssociatedCenter = false;

            if (newClientType === 1) {
                this.isAssociatedCenter = true;
                return;
            }
        }
    }
};
</script>

<style scoped></style>
